import { Component, inject, AfterViewInit, input, computed, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { RippleModule } from '@progress/kendo-angular-ripple';
import { IconsModule } from '@progress/kendo-angular-icons';
import { clipboardCodeIcon, SVGIcon } from '@progress/kendo-svg-icons';
import { ClipboardButtonComponent, MarkdownModule, MarkdownService } from 'ngx-markdown';

import { DynamicSnackBarService } from '../../../../shared/dynamic-snack-bar/dynamic-snack-bar.service';

// Import Prism from index.ts
import Prism from './prismjs-languages-plugins';

/** Matches all `<thinking>…</thinking>` blocks (case-insensitive, global). */
const THINKING_PATTERN = /<thinking>([\s\S]*?)<\/thinking>/gi;

/** Matches all `<tool_use>…</tool_use>` blocks (case-insensitive, global). */
const TOOL_USE_PATTERN = /<tool_use>([\s\S]*?)<\/tool_use>/gi;

/** Matches all `<tool_result>…</tool_result>` blocks (case-insensitive, global). */
const TOOL_RESULT_PATTERN = /<tool_result>([\s\S]*?)<\/tool_result>/gi;

/** All pseudo-XML strip patterns combined — used to clean the main content. */
const ALL_PSEUDO_PATTERNS = [THINKING_PATTERN, TOOL_USE_PATTERN, TOOL_RESULT_PATTERN];

/** Extracts all captured groups from a stateful global regex against the given text. */
function extractBlocks(raw: string | undefined, pattern: RegExp): string[] {
  if (!raw) return [];
  pattern.lastIndex = 0;
  const blocks: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(raw)) !== null) {
    blocks.push(match[1].trim());
  }
  return blocks;
}

@Component({
  selector: 'app-dynamic-markdown',
  imports: [
    MarkdownModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    ButtonsModule,
    IconsModule,
    RippleModule
],
  templateUrl: './dynamic-markdown.component.html',
  styleUrls: ['./dynamic-markdown.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DynamicMarkdownComponent implements AfterViewInit {
  markdownText = input<string>();

  readonly clipboardButton = ClipboardButtonComponent;
  readonly clipboardIcon: SVGIcon = clipboardCodeIcon;

  // #markdownService is needed for us to use the html tag and be able to use the copy button as well as the syntax highlighting.
  // Among other things related to ngx-markdown.
  #markdownService = inject(MarkdownService);
  #snackBarService = inject(DynamicSnackBarService);

  /** Extracted inner text of all `<thinking>` blocks. */
  readonly thoughtBlocks = computed<string[]>(() => extractBlocks(this.markdownText(), THINKING_PATTERN));

  /** Extracted inner text of all `<tool_use>` blocks. */
  readonly toolUseBlocks = computed<string[]>(() => extractBlocks(this.markdownText(), TOOL_USE_PATTERN));

  /** Extracted inner text of all `<tool_result>` blocks. */
  readonly toolResultBlocks = computed<string[]>(() => extractBlocks(this.markdownText(), TOOL_RESULT_PATTERN));

  /** The Markdown text with all pseudo-XML blocks removed. */
  readonly mainContent = computed<string>(() => {
    const raw = this.markdownText();
    if (!raw) return '';
    return ALL_PSEUDO_PATTERNS.reduce((text, pattern) => {
      pattern.lastIndex = 0;
      return text.replace(pattern, '');
    }, raw).trim();
  });

  ngAfterViewInit(): void {
    if (Prism.plugins['autoloader']) {
      Prism.plugins['autoloader'].languages_path = './prismjs-languages-plugins.ts';
      Prism.highlightAll();
    } else {
      console.error('Autoloader plugin is not available.');
    }
  }

  copyToClipboard(): void {
    this.#snackBarService.openSnackBar('Copied code!');
  }
}
